import { HTMLAttributes, useMemo } from "react";
import { cx } from "../../utils/cx";
import styles from "./Progress.module.scss";

export type ProgressSize = "sm" | "md" | "lg";

export interface ProgressProps extends HTMLAttributes<HTMLDivElement> {
    /** Current value (ignored when `indeterminate` is true). */
    value?: number;
    /** Minimum bound (default `0`). */
    min?: number;
    /** Maximum bound (default `100`). */
    max?: number;
    /** Indeterminate activity (no fixed ratio). */
    indeterminate?: boolean;
    /** Track + fill height preset. */
    size?: ProgressSize;
    /** Stretch to 100% of the container width. */
    fullWidth?: boolean;
    /** Non-interactive muted look. */
    disabled?: boolean;
}

function clamp(n: number, min: number, max: number) {
    return Math.min(max, Math.max(min, n));
}

/**
 * Treez DS — Progress (linear).
 *
 * Design source: Figma **Design System** — `Progress` (`7154:50758`, `11148:63272`).
 * Track/fill colors follow the Progress spec: **forest green** fill (`green-10`), not
 * interactive **Sativa** (`primaryMain`). Tokens: `@/theme/tokens.scss`.
 */
export function Progress({
    value = 0,
    min = 0,
    max = 100,
    indeterminate = false,
    size = "md",
    fullWidth = false,
    disabled = false,
    className,
    "aria-label": ariaLabel = "Progress",
    ...rest
}: ProgressProps) {
    const ratio = useMemo(() => {
        if (indeterminate || max <= min) return 0;
        return clamp((value - min) / (max - min), 0, 1);
    }, [value, min, max, indeterminate]);

    const roundedValue = Math.round(value);
    const showZeroDot = !indeterminate && ratio === 0;

    return (
        <div
            className={cx(
                styles.root,
                fullWidth && styles.fullWidth,
                className,
            )}
            data-disabled={disabled || undefined}
            data-indeterminate={indeterminate || undefined}
            data-size={size}
            data-ui="progress"
            {...rest}
        >
            <div
                aria-busy={indeterminate || undefined}
                aria-label={ariaLabel}
                aria-valuemax={indeterminate ? undefined : max}
                aria-valuemin={indeterminate ? undefined : min}
                aria-valuenow={indeterminate ? undefined : roundedValue}
                className={cx(styles.track, showZeroDot && styles.trackZeroDot)}
                role="progressbar"
            >
                <div
                    className={cx(styles.fill, showZeroDot && styles.fillZero)}
                    style={
                        indeterminate
                            ? undefined
                            : showZeroDot
                            ? undefined
                            : { transform: `scaleX(${ratio})` }
                    }
                />
            </div>
        </div>
    );
}

Progress.displayName = "Progress";
